// KPI set/katalog ekranlarının ortak inline stil token'ları (kural 16: yalnız mevcut CSS
// değişkenleri, ham hex yok; kural 17: 44px dokunma hedefi düğmelerde `minHeight`).
import type { CSSProperties } from "react";

export const fieldStyle: CSSProperties = { display: "grid", gap: 4, marginBottom: 12 };

export const labelStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "0.875rem", // TODO-KPI-102: kicker okuması 14 px (eskiden 13)
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-2)",
};

export const inputStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  padding: "8px 10px",
  borderRadius: 8,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--color-border)",
  background: "var(--color-surface)",
  fontSize: "1rem",
  color: "var(--color-text)",
  minHeight: 40,
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

export const monoStyle: CSSProperties = {
  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
  fontSize: "0.875rem",
};

export const btnPrimary: CSSProperties = {
  fontFamily: "var(--font-sans)",
  background: "var(--color-primary)",
  color: "var(--color-surface)",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--color-primary)",
  borderRadius: 8,
  padding: "8px 14px",
  fontSize: "0.875rem",
  fontWeight: 600,
  minHeight: 40,
  cursor: "pointer",
};

export const btnGhost: CSSProperties = {
  fontFamily: "var(--font-sans)",
  background: "transparent",
  color: "var(--color-text)",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--color-border)",
  borderRadius: 8,
  padding: "8px 14px",
  fontSize: "0.875rem",
  minHeight: 40,
  cursor: "pointer",
};

export const btnSmall: CSSProperties = {
  ...btnGhost,
  padding: "4px 10px",
  minHeight: 32,
  fontSize: "0.875rem",
};

export const btnDanger: CSSProperties = {
  ...btnGhost,
  color: "var(--color-error)",
  borderColor: "var(--color-error)",
};

export const pillStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  display: "inline-block",
  padding: "1px 8px",
  borderRadius: 999,
  fontSize: "0.75rem",
  fontWeight: 600,
  background: "color-mix(in srgb, var(--color-primary) 10%, white)",
  color: "var(--color-primary-text)",
  verticalAlign: "middle",
};

/** DR12 rozet sözlüğü — uyarı (amber) pill: yalnız kullanımdan kalktı / kısmi üye / çakışma. */
export const warningPill: CSSProperties = {
  ...pillStyle,
  background: "var(--color-warning-bg)",
  color: "var(--color-warning-text)",
  border: "1px solid var(--color-warning-border)",
};

export const amberBox: CSSProperties = {
  fontFamily: "var(--font-sans)",
  background: "var(--color-warning-bg)",
  color: "var(--color-warning-text)",
  border: "1px solid var(--color-warning-border)",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: "0.875rem",
};

export const errorBox: CSSProperties = {
  fontFamily: "var(--font-sans)",
  background: "color-mix(in srgb, var(--color-error) 8%, white)",
  color: "var(--color-error)",
  border: "1px solid color-mix(in srgb, var(--color-error) 30%, white)",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: "0.875rem",
};

export const thStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  textAlign: "left",
  fontSize: "0.8125rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-3)",
  padding: "8px 12px",
  borderBottom: "1px solid var(--color-border)",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

export const tdStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  padding: "8px 12px",
  borderBottom: "1px solid var(--color-border)",
  fontSize: "0.875rem",
  color: "var(--color-text)",
  verticalAlign: "middle",
};

export const codeStyle: CSSProperties = {
  ...monoStyle,
  fontSize: "0.875rem",
  color: "var(--color-text-2)",
  marginLeft: 6,
};

export const bandRowBg = "var(--color-bg)";
export const selectedRowBg = "color-mix(in srgb, var(--color-primary) 8%, white)";

/** Katalog kök kategori sırası (seed: cevresel, sosyal, yonetisim, ekonomik). */
export const KPI_ROOT_ORDER = ["cevresel", "sosyal", "yonetisim", "ekonomik"] as const;

export const KPI_INCREASE_OPTIONS: { value: "good" | "bad" | "neutral"; label: string }[] = [
  { value: "good", label: "↑ iyi" },
  { value: "bad", label: "↓ iyi" },
  { value: "neutral", label: "nötr" },
];

export function increaseToOption(v: boolean | null): "good" | "bad" | "neutral" {
  return v === null ? "neutral" : v ? "good" : "bad";
}
export function optionToIncrease(v: "good" | "bad" | "neutral"): boolean | null {
  return v === "neutral" ? null : v === "good";
}

/** Hücre editörü popover alanı (KpiCellEditor + ReportedValueBlock ortak; 2026-10-08 ayrımı). */
export const cellInputStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  padding: "7px 10px",
  borderRadius: 8,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  fontSize: "1rem",
  color: "var(--color-text)",
  width: "100%",
};

/** DR11 (birim varyantı) — karar/değer metni 16px. */
export const decisionText: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "1rem",
  color: "var(--color-text)",
};

/** DR11 — yardımcı metin ≥14px, ikincil renk. */
export const helpText: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "0.875rem",
  color: "var(--color-text-2)",
};

/**
 * DR16 — görsel 32 px, dokunma hedefi 44 px. Satır içi stilde ::before yok; teknik: düğme 44×44
 * alanı kaplar (`minHeight/minWidth` 44) ve içeriğini 32 px'lik görsel kutuya (`hitArea44Visual`)
 * alır; negatif kenar boşluğu (−6) yerleşimi 32 px'lik görsele göre korur. Kullanım: düğme
 * `hitArea44`, içindeki span `hitArea44Visual` (+ btnSmall görünümü).
 */
export const hitArea44: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 44,
  minWidth: 44,
  padding: 0,
  margin: -6,
  background: "transparent",
  border: "none",
  cursor: "pointer",
};

/** `hitArea44` içindeki 32 px görsel kutu (btnSmall görünümü). */
export const hitArea44Visual: CSSProperties = {
  ...btnSmall,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

/** Pencere alt şeridi eylem düğmesi — 44 px (DR16). `primary` doldurulu, aksi ghost. */
export function footerBtn(primary = false): CSSProperties {
  return {
    ...(primary ? btnPrimary : btnGhost),
    minHeight: 44,
    minWidth: 44,
    padding: "10px 18px",
  };
}

/**
 * Birim yüzeyleri tablo başlığı (TODO-KPI-102): `thStyle`'ın daha koyu başlık rengi. Ortak
 * `thStyle` değişmez — katalog senkron bandının "paketsiz bant bayt kilidi" onu kullanır.
 */
export const thStyleStrong: CSSProperties = { ...thStyle, color: "var(--color-text-2)" };
